import { useEffect, useMemo, useState } from 'react'
import { Pencil, Plus, X } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import {
  useUpdateRepositoryAsset,
  useLinkRepositoryAssetToProduct,
  useUnlinkRepositoryAssetFromProduct,
  useProducts,
  useRepositoryAsset,
} from '@/hooks/use-api'
import { usePicklistOptions } from '@/hooks/use-picklists'
import type { RepositoryAsset, RepositorySection } from '@/types'
import { explainError } from '@/lib/explain-error'

interface Props {
  asset: RepositoryAsset
  open: boolean
  onClose: () => void
}

export function RepositoryAssetEditModal({ asset, open, onClose }: Props) {
  const { toast } = useToast()

  const SECTION_OPTIONS = usePicklistOptions('content', 'repository_section')
  const ASSET_TYPES = usePicklistOptions('content', 'asset_type')

  const update = useUpdateRepositoryAsset()
  const link = useLinkRepositoryAssetToProduct()
  const unlink = useUnlinkRepositoryAssetFromProduct()

  // Re-fetch the asset so we get the freshest products[] (with link_id) for unlink.
  const { data: detail } = useRepositoryAsset(open ? asset.id : undefined)
  const fresh = (detail?.data as RepositoryAsset | undefined) ?? asset

  const { data: productsResponse } = useProducts({ limit: 1000 })
  const allProducts = productsResponse?.data || []

  const [section, setSection] = useState<RepositorySection>(asset.repository_section || 'Company')
  const [assetType, setAssetType] = useState<string>(asset.asset_type || ASSET_TYPES[0] || 'Other')
  const [summary, setSummary] = useState<string>(asset.repository_summary || '')
  const [tags, setTags] = useState<string[]>(asset.tags || [])
  const [tagInput, setTagInput] = useState('')

  const [newProductId, setNewProductId] = useState<string>('')
  const [newProductRole, setNewProductRole] = useState<string>(asset.asset_type || 'Brochure')

  // Reset state when a different asset opens.
  useEffect(() => {
    setSection(asset.repository_section || 'Company')
    setAssetType(asset.asset_type || ASSET_TYPES[0] || 'Other')
    setSummary(asset.repository_summary || '')
    setTags(asset.tags || [])
    setTagInput('')
    setNewProductId('')
    setNewProductRole(asset.asset_type || 'Brochure')
  }, [asset.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const linkedProducts = fresh.products || []

  const availableProducts = useMemo(() => {
    const linkedIds = new Set(linkedProducts.map((p) => p.product_id))
    return allProducts.filter((p) => !linkedIds.has(p.id))
  }, [allProducts, linkedProducts])

  const handleAddTag = () => {
    const t = tagInput.trim()
    if (!t) return
    if (tags.includes(t)) {
      setTagInput('')
      return
    }
    setTags([...tags, t])
    setTagInput('')
  }

  const handleSave = async () => {
    try {
      await update.mutateAsync({
        id: asset.id,
        payload: {
          repository_section: section,
          asset_type: assetType,
          repository_summary: summary || null,
          tags,
        },
      })
      toast({ title: 'Saved', description: 'Repository details updated' })
      onClose()
    } catch (err) {
      toast({
        title: 'Could not save',
        description: explainError(err, 'Unknown error'),
        variant: 'destructive',
      })
    }
  }

  const handleAddProduct = async () => {
    if (!newProductId || !newProductRole) return
    try {
      await link.mutateAsync({
        id: asset.id,
        payload: { product_id: newProductId, role: newProductRole },
      })
      setNewProductId('')
      toast({ title: 'Linked', description: 'Product added' })
    } catch (err) {
      toast({
        title: 'Could not link product',
        description: explainError(err, 'Unknown error'),
        variant: 'destructive',
      })
    }
  }

  const handleRemoveProduct = async (linkId?: string) => {
    if (!linkId) return
    try {
      await unlink.mutateAsync({ id: asset.id, linkId })
      toast({ title: 'Unlinked' })
    } catch (err) {
      toast({
        title: 'Could not unlink',
        description: explainError(err, 'Unknown error'),
        variant: 'destructive',
      })
    }
  }

  const switchingToCompanyWithLinks = section === 'Company' && linkedProducts.length > 0

  return (
    <Dialog open={open} onOpenChange={(v) => (!v ? onClose() : null)}>
      <DialogContent className="max-w-2xl" aria-describedby="repository-edit-description">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="h-5 w-5" aria-hidden="true" />
            Edit repository details
          </DialogTitle>
          <DialogDescription id="repository-edit-description">
            Update the section, type, summary, and tags. Product-section assets can be linked to
            products below.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="repo-section">Section</Label>
              <Select value={section} onValueChange={(v) => setSection(v as RepositorySection)}>
                <SelectTrigger id="repo-section" aria-label="Repository section">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SECTION_OPTIONS.map((opt) => (
                    <SelectItem key={opt} value={opt}>
                      {opt}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {switchingToCompanyWithLinks && (
                <p className="text-xs text-destructive mt-1" role="alert">
                  Remove product links below before switching to the Company section.
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="repo-asset-type">Asset type</Label>
              <Select value={assetType} onValueChange={setAssetType}>
                <SelectTrigger id="repo-asset-type" aria-label="Asset type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ASSET_TYPES.map((opt) => (
                    <SelectItem key={opt} value={opt}>
                      {opt}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="repo-summary">Summary</Label>
            <textarea
              id="repo-summary"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="Long-form description shown when browsing the repository"
              className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              aria-label="Asset summary"
            />
          </div>

          <div>
            <Label htmlFor="repo-tags">Tags</Label>
            <div className="flex gap-2">
              <Input
                id="repo-tags"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleAddTag()
                  }
                }}
                placeholder="Add a tag and press Enter"
                aria-label="Add tag"
              />
              <Button type="button" variant="outline" onClick={handleAddTag}>
                Add
              </Button>
            </div>
            {tags.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {tags.map((tag) => (
                  <Badge key={tag} variant="outline" className="flex items-center gap-1">
                    {tag}
                    <button
                      type="button"
                      onClick={() => setTags(tags.filter((t) => t !== tag))}
                      aria-label={`Remove tag ${tag}`}
                      className="hover:text-destructive"
                    >
                      <X className="h-3 w-3" aria-hidden="true" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {section === 'Product' && (
            <div className="rounded-md border p-3">
              <div className="text-sm font-medium mb-2">Products</div>
              {linkedProducts.length === 0 && (
                <p className="text-xs text-muted-foreground mb-2">No products linked yet.</p>
              )}
              {linkedProducts.length > 0 && (
                <ul className="space-y-1 mb-3">
                  {linkedProducts.map((p) => (
                    <li
                      key={`${p.product_id}-${p.role}`}
                      className="flex items-center justify-between gap-2 text-sm"
                    >
                      <span>
                        {p.product_name || p.product_id}{' '}
                        <Badge variant="outline" className="ml-1">
                          {p.role}
                        </Badge>
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveProduct(p.link_id)}
                        aria-label={`Unlink ${p.product_name || p.product_id}`}
                      >
                        <X className="h-4 w-4" aria-hidden="true" />
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
              <div className="flex flex-col gap-2 md:flex-row md:items-end">
                <div className="flex-1">
                  <Label htmlFor="repo-new-product" className="text-xs">
                    Add product
                  </Label>
                  <Select value={newProductId} onValueChange={setNewProductId}>
                    <SelectTrigger id="repo-new-product" aria-label="Choose product">
                      <SelectValue placeholder="Choose a product" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableProducts.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:w-44">
                  <Label htmlFor="repo-new-role" className="text-xs">
                    Role
                  </Label>
                  <Select value={newProductRole} onValueChange={setNewProductRole}>
                    <SelectTrigger id="repo-new-role" aria-label="Document role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ASSET_TYPES.map((opt) => (
                        <SelectItem key={opt} value={opt}>
                          {opt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  type="button"
                  onClick={handleAddProduct}
                  disabled={!newProductId || link.isPending}
                >
                  <Plus className="mr-1 h-4 w-4" aria-hidden="true" />
                  Add
                </Button>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              disabled={update.isPending || switchingToCompanyWithLinks}
            >
              {update.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
